import { ArrowRight } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1714636608872-048fc9231892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwYmVpZ2UlMjB0ZXh0dXJlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODA2NzU2MjR8MA&ixlib=rb-4.1.0&q=85";
const HERO_PORTRAIT = "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?crop=entropy&cs=tinysrgb&fm=jpg&w=900&q=85";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          backgroundImage: `url('${HERO_BG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-melissa-bg/0 via-melissa-bg/40 to-melissa-bg" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7 reveal" style={{ animationDelay: "0.1s" }}>
          <p className="text-xs uppercase tracking-overline text-melissa-inkSoft mb-8">
            <span data-testid="hero-overline">Elevated Beauty. Unforgettable Moments.</span>
          </p>
          <h1 className="font-serif font-light text-melissa-ink text-[3.25rem] sm:text-7xl lg:text-[8.5rem] leading-[0.92] tracking-tighter">
            <span className="block">Refined</span>
            <span className="block italic text-melissa-rose">artistry,</span>
            <span className="block">timeless faces.</span>
          </h1>
          <p className="max-w-xl mt-10 text-base md:text-lg text-melissa-inkSoft font-light leading-relaxed">
            A curated beauty practice for the moments that matter — bridal, editorial,
            and the days you simply want to feel like yourself, only more so.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-6">
            <a
              href="#contact"
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-melissa-ink text-melissa-bg text-xs uppercase tracking-overline hover:bg-melissa-rose transition-colors duration-300"
            >
              Reserve a Date
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#portfolio"
              data-testid="hero-portfolio-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-overline text-melissa-ink border-b border-melissa-ink/30 pb-1 hover:border-melissa-ink transition-all"
            >
              View the Portfolio
            </a>
          </div>

          <div className="mt-16 grid grid-cols-3 gap-6 max-w-md">
            <div>
              <div className="font-serif text-4xl text-melissa-ink">12+</div>
              <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft mt-1">Years of practice</div>
            </div>
            <div>
              <div className="font-serif text-4xl text-melissa-ink">400+</div>
              <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft mt-1">Brides served</div>
            </div>
            <div>
              <div className="font-serif text-4xl text-melissa-ink">28</div>
              <div className="text-[10px] uppercase tracking-overline text-melissa-inkSoft mt-1">Editorials</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 reveal" style={{ animationDelay: "0.35s" }}>
          <div className="relative aspect-[3/4] overflow-hidden">
            <img
              src={HERO_PORTRAIT}
              alt="Bridal makeup portrait"
              data-testid="hero-portrait"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-6 left-6 right-6 bg-melissa-bg/85 backdrop-blur-md p-4">
              <p className="text-[10px] uppercase tracking-overline text-melissa-inkSoft">Featured</p>
              <p className="font-serif text-lg text-melissa-ink mt-1">The Quiet Bride · Spring Collection</p>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-24 border-y border-melissa-border py-6 overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap gap-16 text-melissa-inkSoft text-xs uppercase tracking-overline">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16 shrink-0">
              <span>Bridal</span><span aria-hidden>—</span>
              <span>Editorial</span><span aria-hidden>—</span>
              <span>Photoshoot</span><span aria-hidden>—</span>
              <span>HD Makeup</span><span aria-hidden>—</span>
              <span>Engagement</span><span aria-hidden>—</span>
              <span>Soft Glam</span><span aria-hidden>—</span>
              <span>Natural Beauty</span><span aria-hidden>—</span>
              <span>1-on-1 Lessons</span><span aria-hidden>—</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
